<template>
  <el-dialog
    v-dialog-drag-resize
    :visible.sync="dialogVisible"
    width="80%"
    top="5vh"
    custom-class="fp-dialog"
    :close-on-click-modal="false"
    append-to-body
    @closed="handleClosed"
  >
    <template #title>
      <div class="fp-header">
        <div class="fp-header-icon" :style="{ background: headerBg }">
          <i :class="headerIcon"></i>
        </div>
        <div class="fp-header-info">
          <span class="fp-header-name">{{ fileName }}</span>
          <span v-if="isZipMode && fileCount > 0" class="fp-header-badge">共 {{ fileCount }} 个文件</span>
        </div>
      </div>
    </template>

    <div class="fp-body" v-loading="loading" element-loading-text="正在加载文件内容...">
      <!-- 错误状态 -->
      <div v-if="errorMsg" class="fp-center-state">
        <div class="fp-center-icon fp-center-icon--error"><i class="el-icon-warning-outline"></i></div>
        <p class="fp-center-title">{{ errorMsg }}</p>
        <el-button type="primary" size="small" plain @click="retry">
          <i class="el-icon-refresh"></i> 重新加载
        </el-button>
      </div>

      <!-- ZIP 压缩包浏览模式 -->
      <div v-else-if="isZipMode" class="fp-zip">
        <!-- 侧边栏：文件树 -->
        <div class="fp-sidebar" :class="{ 'fp-sidebar--fold': sidebarFold }">
          <div class="fp-sidebar-top">
            <template v-if="!sidebarFold">
              <i class="el-icon-folder-opened"></i>
              <span>文件列表</span>
            </template>
            <el-button type="text" size="mini" :icon="sidebarFold ? 'el-icon-s-unfold' : 'el-icon-s-fold'" @click="sidebarFold = !sidebarFold" />
          </div>
          <div v-show="!sidebarFold" class="fp-sidebar-scroll">
            <el-tree
              ref="fileTree"
              :data="treeData"
              :props="{ children: 'children', label: 'name' }"
              node-key="path"
              :default-expand-all="treeData.length <= 80"
              highlight-current
              :expand-on-click-node="false"
              @node-click="onNodeClick"
            >
              <span slot-scope="{ data }" class="fp-node" :class="{ 'fp-node--active': activeFile && activeFile.path === data.path }">
                <i :class="data.icon || 'el-icon-folder'" :style="{ color: data.color || '#f59e0b' }"></i>
                <span class="fp-node-text" :title="data.name">{{ data.name }}</span>
              </span>
            </el-tree>
          </div>
        </div>

        <!-- 预览区 -->
        <div class="fp-main">
          <!-- 空状态 -->
          <div v-if="!activeFile" class="fp-center-state">
            <div class="fp-center-icon"><i class="el-icon-document-checked"></i></div>
            <p class="fp-center-title">请从左侧选择文件进行预览</p>
          </div>
          <!-- 图片 -->
          <div v-else-if="activeType === 'image'" class="fp-view fp-view--image">
            <img :src="activeBlobUrl" :alt="activeFile.name" />
          </div>
          <!-- PDF -->
          <div v-else-if="activeType === 'pdf'" class="fp-view fp-view--doc">
            <iframe :src="activeBlobUrl" frameborder="0"></iframe>
          </div>
          <!-- 文本 -->
          <div v-else-if="activeType === 'text'" class="fp-view fp-view--text">
            <pre>{{ activeText }}</pre>
          </div>
          <!-- 视频 -->
          <div v-else-if="activeType === 'video'" class="fp-view fp-view--video">
            <video :src="activeBlobUrl" controls></video>
          </div>
          <!-- 不支持在线预览（Office 等）：提供保存到电脑 -->
          <div v-else class="fp-center-state">
            <div class="fp-center-icon fp-center-icon--warn"><i class="el-icon-document"></i></div>
            <p class="fp-center-title">{{ activeFile.name }}</p>
            <p class="fp-center-desc">该文件类型需保存到电脑后使用对应软件打开</p>
            <el-button type="primary" size="small" @click="downloadActive">
              <i class="el-icon-download"></i> 保存到电脑
            </el-button>
          </div>
        </div>
      </div>

      <!-- 非 ZIP 文件：直接预览 -->
      <div v-else class="fp-direct">
        <!-- Office 文件：微软在线查看器 -->
        <iframe
          v-if="officeViewerUrl"
          :src="officeViewerUrl"
          frameborder="0"
          class="fp-direct-frame"
          @load="loading = false"
        ></iframe>
        <!-- PDF：blob URL iframe -->
        <iframe
          v-else-if="directBlobUrl"
          :src="directBlobUrl"
          frameborder="0"
          class="fp-direct-frame"
          @load="loading = false"
        ></iframe>
        <!-- 图片：blob URL img -->
        <div v-else-if="directImageUrl" class="fp-direct-image">
          <img :src="directImageUrl" />
        </div>
        <!-- 文本：blob URL 读取 -->
        <div v-else-if="directTextContent" class="fp-direct-text">
          <pre>{{ directTextContent }}</pre>
        </div>
      </div>
    </div>

    <template #footer>
      <el-button size="small" @click="dialogVisible = false">关 闭</el-button>
    </template>
  </el-dialog>
</template>

<script>
import JSZip from 'jszip'

// 文件类型映射表
const TYPE_MAP = [
  { exts: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'], icon: 'el-icon-picture', color: '#10b981', type: 'image' },
  { exts: ['pdf'], icon: 'el-icon-document', color: '#ef4444', type: 'pdf' },
  { exts: ['txt', 'csv', 'json', 'xml', 'log', 'md', 'html', 'css', 'js', 'ini', 'yaml', 'yml'], icon: 'el-icon-tickets', color: '#6366f1', type: 'text' },
  { exts: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'], icon: 'el-icon-video-camera', color: '#8b5cf6', type: 'video' },
  { exts: ['mp3', 'wav', 'ogg', 'flac', 'aac'], icon: 'el-icon-headset', color: '#f59e0b', type: 'audio' },
  { exts: ['doc', 'docx'], icon: 'el-icon-document', color: '#2b579a', type: 'word' },
  { exts: ['xls', 'xlsx'], icon: 'el-icon-document', color: '#217346', type: 'excel' },
  { exts: ['ppt', 'pptx'], icon: 'el-icon-data-board', color: '#d24726', type: 'ppt' }
]

function getType (name) {
  const ext = (name.split('.').pop() || '').toLowerCase()
  return TYPE_MAP.find(m => m.exts.includes(ext)) || { icon: 'el-icon-document', color: '#94a3b8', type: 'other' }
}

export default {
  name: 'FilePreview',
  props: {
    visible: { type: Boolean, default: false },
    url: { type: String, default: '' },
    title: { type: String, default: '' }
  },
  data () {
    return {
      loading: false,
      errorMsg: '',
      isZipMode: false,
      // zip 相关
      zipInstance: null,
      treeData: [],
      fileCount: 0,
      sidebarFold: false,
      // 当前预览
      activeFile: null,
      activeType: '',
      activeBlobUrl: '',
      activeText: '',
      // 非 zip 直接预览
      directBlobUrl: '',
      directImageUrl: '',
      directTextContent: '',
      officeViewerUrl: ''
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.visible },
      set (v) { this.$emit('update:visible', v) }
    },
    fileName () {
      return this.title || (this.url ? decodeURIComponent(this.url.split('/').pop().split('?')[0]) : '文件预览')
    },
    headerIcon () {
      if (this.isZipMode) return 'el-icon-folder-opened'
      const ext = (this.fileName.split('.').pop() || '').toLowerCase()
      const info = getType(this.fileName)
      if (['word', 'excel', 'ppt'].includes(info.type)) return info.icon
      return 'el-icon-document'
    },
    headerBg () {
      if (this.isZipMode) return 'linear-gradient(135deg, #f59e0b, #f97316)'
      const info = getType(this.fileName)
      const bgMap = { word: 'linear-gradient(135deg, #2b579a, #3b82f6)', excel: 'linear-gradient(135deg, #217346, #10b981)', ppt: 'linear-gradient(135deg, #d24726, #ef4444)', pdf: 'linear-gradient(135deg, #ef4444, #f97316)' }
      return bgMap[info.type] || 'linear-gradient(135deg, #3b82f6, #6366f1)'
    }
  },
  watch: {
    // 仅监听 visible，父组件同时设置 url+visible 时不会重复触发 init
    visible (v) {
      if (v && this.url) this.init()
    }
  },
  methods: {
    // 初始化预览
    init () {
      this.reset()
      if (!this.url) return
      this.loading = true
      this.errorMsg = ''

      const ext = (this.fileName.split('.').pop() || '').toLowerCase()
      if (ext === 'zip') {
        this.isZipMode = true
        this.loadZip()
      } else if (ext === 'rar') {
        this.loading = false
        this.errorMsg = 'RAR 格式暂不支持在线浏览，请下载后使用解压软件查看'
      } else {
        this.openDirect(ext)
      }
    },

    reset () {
      this.releaseBlob()
      this.releaseDirectBlob()
      this.zipInstance = null
      this.treeData = []
      this.fileCount = 0
      this.sidebarFold = false
      this.activeFile = null
      this.activeType = ''
      this.activeText = ''
      this.directBlobUrl = ''
      this.directImageUrl = ''
      this.directTextContent = ''
      this.officeViewerUrl = ''
      this.isZipMode = false
      this.errorMsg = ''
    },

    releaseBlob () {
      if (this.activeBlobUrl) {
        URL.revokeObjectURL(this.activeBlobUrl)
        this.activeBlobUrl = ''
      }
    },

    releaseDirectBlob () {
      if (this.directBlobUrl) {
        URL.revokeObjectURL(this.directBlobUrl)
        this.directBlobUrl = ''
      }
      if (this.directImageUrl) {
        URL.revokeObjectURL(this.directImageUrl)
        this.directImageUrl = ''
      }
    },

    // 下载文件为 ArrayBuffer（通过 /file-download 代理绕过 CORS）
    fetchArrayBuffer (url) {
      return new Promise((resolve, reject) => {
        // 开发环境由 webpack-dev-server 代理，生产环境由 nginx 代理
        const fetchUrl = url.replace(/^https?:\/\/[^/]+/, '/file-download')
        const xhr = new XMLHttpRequest()
        xhr.open('GET', fetchUrl, true)
        xhr.responseType = 'arraybuffer'
        xhr.timeout = 180000
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response)
          } else {
            reject(new Error('下载失败 (HTTP ' + xhr.status + ')'))
          }
        }
        xhr.onerror = () => reject(new Error('网络请求失败，请检查网络连接'))
        xhr.ontimeout = () => reject(new Error('下载超时，文件可能较大'))
        xhr.send()
      })
    },

    // 加载 zip 压缩包
    async loadZip () {
      try {
        const buffer = await this.fetchArrayBuffer(this.url)
        if (!buffer || buffer.byteLength < 22) {
          throw new Error('文件数据为空')
        }
        // 校验 zip 文件头（PK 开头）
        const head = new Uint8Array(buffer.slice(0, 4))
        if (head[0] !== 0x50 || head[1] !== 0x4B) {
          throw new Error('返回的数据不是有效的 zip 文件，可能是文件地址无效或服务端返回了错误页面')
        }
        const zip = await JSZip.loadAsync(buffer)
        this.zipInstance = zip
        this.buildTree(zip)
      } catch (err) {
        this.errorMsg = err.message || '压缩包解析失败'
        console.error('[FilePreview] zip 解析失败:', err)
      } finally {
        this.loading = false
      }
    },

    // 构建 zip 内文件树
    buildTree (zip) {
      const root = []
      let count = 0
      zip.forEach((relativePath, entry) => {
        if (entry.dir) return
        count++
        const parts = relativePath.split('/')
        let cursor = { children: root }
        parts.forEach((seg, i) => {
          if (i === parts.length - 1) {
            const info = getType(seg)
            cursor.children.push({ name: seg, path: relativePath, isFile: true, icon: info.icon, color: info.color, fileType: info.type })
          } else {
            let folder = cursor.children.find(c => c.name === seg && !c.isFile)
            if (!folder) {
              folder = { name: seg, path: parts.slice(0, i + 1).join('/'), isFile: false, icon: 'el-icon-folder', color: '#f59e0b', children: [] }
              cursor.children.push(folder)
            }
            cursor = folder
          }
        })
      })
      const sortNodes = (arr) => {
        arr.sort((a, b) => (a.isFile === b.isFile ? a.name.localeCompare(b.name, 'zh') : a.isFile ? 1 : -1))
        arr.forEach(n => n.children && sortNodes(n.children))
      }
      sortNodes(root)
      this.treeData = root
      this.fileCount = count
    },

    // 点击文件树节点
    async onNodeClick (node) {
      if (!node.isFile) return
      this.releaseBlob()
      this.activeFile = node
      this.activeType = node.fileType
      this.activeText = ''
      try {
        const entry = this.zipInstance.file(node.path)
        if (!entry) return
        // 文本类型：读取字符串内容用于行内展示
        if (node.fileType === 'text') {
          this.activeText = await entry.async('string')
        }
        // 所有类型都创建 blob URL（用于预览 / 新标签页打开 / 下载）
        const blob = await entry.async('blob')
        this.activeBlobUrl = URL.createObjectURL(blob)
      } catch (e) {
        this.$message.error('文件读取失败')
      }
    },

    // 在新标签页打开（浏览器自带查看器或插件处理）
    openInNewTab () {
      if (!this.activeBlobUrl) return
      window.open(this.activeBlobUrl, '_blank')
    },

    // 下载 zip 内单个文件：优先弹系统另存为对话框，用户自选保存位置
    async downloadActive () {
      if (!this.activeFile || !this.activeBlobUrl) return
      try {
        const blob = await fetch(this.activeBlobUrl).then(r => r.blob())
        // 浏览器支持系统文件保存对话框（Chrome 86+ / Edge 86+）
        if (window.showSaveFilePicker) {
          try {
            const ext = this.activeFile.name.split('.').pop() || ''
            const handle = await window.showSaveFilePicker({
              suggestedName: this.activeFile.name,
              types: ext ? [{ description: ext.toUpperCase() + ' 文件', accept: { 'application/octet-stream': ['.' + ext] } }] : undefined
            })
            const writable = await handle.createWritable()
            await writable.write(blob)
            await writable.close()
            this.$message.success('「' + this.activeFile.name + '」已保存到所选位置')
            return
          } catch (err) {
            // 用户取消了保存对话框
            if (err.name === 'AbortError') return
          }
        }
        // 降级：传统下载方式
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = this.activeBlobUrl
        a.download = this.activeFile.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        this.$message({ type: 'success', message: '「' + this.activeFile.name + '」已下载，可在浏览器下载栏（Ctrl+J）查看', duration: 5000 })
      } catch (e) {
        this.$message.error('下载失败')
      }
    },

    // 非 zip 文件：通过代理下载后用 blob URL 预览，绕过 CORS
    async openDirect (ext) {
      const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
      const textExts = ['txt', 'csv', 'json', 'xml', 'log', 'md', 'html', 'css', 'js', 'ini', 'yaml', 'yml']
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']

      // Office 文件：优先使用微软在线查看器（需要文件公网可访问）
      if (officeExts.includes(ext)) {
        this.officeViewerUrl = 'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(this.url)
        this.loading = false
        return
      }

      // PDF / 图片 / 文本：通过代理下载后用 blob URL 预览
      try {
        const buffer = await this.fetchArrayBuffer(this.url)
        const blob = new Blob([buffer])
        const blobUrl = URL.createObjectURL(blob)

        if (imageExts.includes(ext)) {
          this.directImageUrl = blobUrl
        } else if (textExts.includes(ext)) {
          // 文本文件读取为字符串
          this.directTextContent = await blob.text()
          URL.revokeObjectURL(blobUrl)
        } else {
          // PDF 和其他文件用 iframe
          this.directBlobUrl = blobUrl
        }
      } catch (err) {
        // 代理下载失败时降级为直接 URL（可能因 CORS 失败）
        this.directBlobUrl = this.url
        console.warn('[FilePreview] 代理下载失败，降级使用直链:', err.message)
      } finally {
        this.loading = false
      }
    },

    // 弹框关闭时清理资源
    handleClosed () {
      this.reset()
    },

    retry () {
      this.init()
    }
  }
}
</script>

<style lang="scss" scoped>
/* ========== 弹框 ========== */
::v-deep .fp-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header { padding: 0; border-bottom: 1px solid #eef1f6; }
  .el-dialog__body { padding: 0; height: 72vh; overflow: hidden; }
  .el-dialog__footer { padding: 12px 24px; border-top: 1px solid #eef1f6; }
}

/* ========== 顶部标题 ========== */
.fp-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 24px;
}

.fp-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i { font-size: 18px; color: #fff; }
}

.fp-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.fp-header-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fp-header-badge {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

/* ========== 主体容器 ========== */
.fp-body {
  width: 100%;
  height: 100%;
}

/* ========== 居中状态（空/错误/不支持） ========== */
.fp-center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
  padding: 40px;
}

.fp-center-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0f5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  i { font-size: 32px; color: #3b82f6; }

  &--error {
    background: #fef2f2;
    i { color: #ef4444; }
  }

  &--warn {
    background: #fffbeb;
    i { color: #f59e0b; }
  }
}

.fp-center-title {
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  margin: 0;
  text-align: center;
}

.fp-center-desc {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* ========== ZIP 布局 ========== */
.fp-zip {
  display: flex;
  height: 100%;
}

/* ========== 侧边栏 ========== */
.fp-sidebar {
  width: 280px;
  min-width: 280px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease, min-width 0.2s ease;

  &--fold {
    width: 48px;
    min-width: 48px;
  }
}

.fp-sidebar-top {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #475569;

  i { margin-right: 6px; color: #3b82f6; }
}

.fp-sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
}

/* ========== 文件树节点 ========== */
.fp-node {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 2px 0;
  overflow: hidden;

  i { flex-shrink: 0; font-size: 14px; }

  &--active { font-weight: 600; }
}

.fp-node-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ========== 主预览区 ========== */
.fp-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* ========== 预览视图 ========== */
.fp-view {
  flex: 1;
  overflow: auto;

  &--image {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #f1f5f9;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 6px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }
  }

  &--doc {
    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  }

  &--text {
    background: #f8fafc;

    pre {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 20px;
      font-size: 13px;
      line-height: 1.7;
      color: #334155;
      white-space: pre-wrap;
      word-break: break-all;
      font-family: Consolas, Monaco, 'Courier New', monospace;
    }
  }

  &--video {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #000;

    video {
      max-width: 100%;
      max-height: 100%;
      border-radius: 4px;
    }
  }
}

/* ========== 非 ZIP 直接预览 ========== */
.fp-direct {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fp-direct-frame {
  width: 100%;
  height: 72vh;
  border: none;
}

.fp-direct-image {
  width: 100%;
  height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f1f5f9;
  overflow: auto;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
}

.fp-direct-text {
  width: 100%;
  height: 72vh;
  background: #f8fafc;
  overflow: auto;

  pre {
    margin: 0;
    padding: 20px;
    font-size: 13px;
    line-height: 1.7;
    color: #334155;
    white-space: pre-wrap;
    word-break: break-all;
    font-family: Consolas, Monaco, 'Courier New', monospace;
  }
}

/* ========== 居中状态按钮组 ========== */
.fp-center-btns {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}
</style>
